<template>
    <v-card
        :class="cardClasses"
        :loading="loading"
        outlined
        hover
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
    >
        <template v-if="loading">
            <v-skeleton-loader
                type="card-heading, list-item-avatar-two-line, actions"
                height="180"
            />
        </template>

        <template v-else>
            <v-card-title class="py-2 d-flex align-center">
                <v-icon left :color="occupationColorValue" size="20">
                    {{ ambienceIcon }}
                </v-icon>
                <span class="subtitle-1 font-weight-medium text-truncate">
                    {{ ambience.name }}
                </span>
                <v-spacer />
                <v-chip
                    x-small
                    :color="occupationColorValue"
                    dark
                    class="ml-2"
                >
                    {{ occupationPercentageValue }}%
                </v-chip>
            </v-card-title>

            <v-divider />

            <v-card-text class="py-2">
                <v-row dense>
                    <v-col cols="6">
                        <div class="caption grey--text d-flex align-center">
                            Capacidade
                            <v-tooltip v-if="capacityModeLabel" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                        v-bind="attrs"
                                        v-on="on"
                                        x-small
                                        outlined
                                        class="ml-1 capacity-mode-chip"
                                    >
                                        {{ capacityModeLabel }}
                                    </v-chip>
                                </template>
                                <span>Modo de capacidade ativo</span>
                            </v-tooltip>
                        </div>
                        <div class="body-2 font-weight-medium">
                            {{ activeCapacityValue }} lugares
                        </div>
                    </v-col>
                    <v-col cols="6">
                        <div class="caption grey--text">Ocupação</div>
                        <div class="body-2 font-weight-medium">
                            {{ occupation.students || 0 }} alunos
                        </div>
                    </v-col>
                </v-row>

                <v-progress-linear
                    :value="occupationPercentageValue"
                    :color="occupationColorValue"
                    height="8"
                    rounded
                    class="mt-2"
                />

                <v-row dense class="mt-2" v-if="hasBalance">
                    <v-col cols="6">
                        <div class="caption grey--text">Previsão</div>
                        <div class="body-2">{{ occupation.forecast || 0 }}</div>
                    </v-col>
                    <v-col cols="6">
                        <div class="caption grey--text">Saldo</div>
                        <v-chip
                            x-small
                            :color="balanceColorValue"
                            :text-color="balanceTextColor"
                            outlined
                        >
                            {{ occupation.balance > 0 ? '+' : '' }}{{ occupation.balance || 0 }}
                        </v-chip>
                    </v-col>
                </v-row>

                <div v-if="hasResources" class="mt-2">
                    <sys-ambience-resource-tags
                        :resources="ambience.resources"
                        x-small
                        icon-only
                        :max-display="5"
                    />
                </div>

                <div
                    v-if="teacher"
                    class="mt-3 d-flex align-center teacher-info"
                    @click.stop="$emit('click:teacher', teacher)"
                >
                    <v-avatar size="32" color="grey lighten-2" class="mr-2 cursor-pointer">
                        <v-img v-if="teacher.photo" :src="teacher.photo" />
                        <v-icon v-else color="grey">mdi-account</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1 text-truncate">
                        <div class="caption grey--text">Docente</div>
                        <div class="body-2 text-truncate">{{ teacher.name }}</div>
                    </div>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-2">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            small
                            text
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="$emit('click:details', ambience)"
                        >
                            <v-icon small left>mdi-eye</v-icon>
                            Detalhes
                        </v-btn>
                    </template>
                    <span>Ver detalhes da sala</span>
                </v-tooltip>

                <v-spacer />

                <v-tooltip bottom v-if="showSwapAction">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            small
                            icon
                            :color="isHovered ? 'orange' : 'grey'"
                            v-bind="attrs"
                            v-on="on"
                            @click="$emit('click:suggest-swap', ambience)"
                        >
                            <v-icon small>mdi-swap-horizontal</v-icon>
                        </v-btn>
                    </template>
                    <span>Sugerir troca de sala</span>
                </v-tooltip>
            </v-card-actions>
        </template>
    </v-card>
</template>

<script>
import {
    getActiveCapacity,
    getBalanceColor,
    getCapacityModeLabel,
    getOccupationColor,
    getOccupationPercentage,
} from './helpers/occupationStatus'

export default {
    name: "SysAmbienceOccupationCard",

    data: () => ({
        isHovered: false
    }),

    props: {
        ambience: {
            type: Object,
            required: true
        },
        occupation: {
            type: Object,
            default: () => ({})
        },
        teacher: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        showSwapAction: {
            type: Boolean,
            default: true
        }
    },

    computed: {
        cardClasses() {
            return {
                'ambience-card': true,
                'ambience-card--hovered': this.isHovered,
                'ambience-card--loading': this.loading
            }
        },

        ambienceIcon() {
            return this.ambience.type?.is_virtual ? 'mdi-monitor-dashboard' : 'mdi-door'
        },

        activeCapacityValue() {
            return getActiveCapacity(this.ambience)
        },

        capacityModeLabel() {
            return getCapacityModeLabel(this.ambience.capacity_mode)
        },

        occupationPercentageValue() {
            return Math.min(getOccupationPercentage(this.ambience, this.occupation), 100)
        },

        occupationColorValue() {
            return getOccupationColor(this.ambience, this.occupation)
        },

        hasBalance() {
            return this.occupation.balance != null
        },

        hasResources() {
            return Array.isArray(this.ambience.resources) && this.ambience.resources.length > 0
        },

        balanceColorValue() {
            return getBalanceColor(this.occupation.balance)
        },

        balanceTextColor() {
            return this.occupation.balance ? 'white' : 'grey'
        }
    }
}
</script>

<style scoped>
.ambience-card {
    transition: all 0.2s ease-in-out;
    min-height: 180px;
}

.ambience-card--hovered {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.ambience-card--loading {
    pointer-events: none;
}

.teacher-info {
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.teacher-info:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.cursor-pointer {
    cursor: pointer;
}

.capacity-mode-chip {
    height: 16px !important;
    font-size: 0.625rem !important;
}
</style>
